import { gql } from '@apollo/client';
import { compareDesc, parseISO } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import * as React from 'react';

import { Tab } from 'play/atoms/tab/Tab';
import { HeaderLegacy } from 'play/legacy/organisms/header-legacy/HeaderLegacy';
import { ExpiredScreen } from 'play/organisms/expired-screen/ExpiredScreen';
import { TabContent } from 'play/organisms/workspace/TabContent';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import {
  ChallengeTabType,
  LearnEnvironmentLegacyQuery,
  LearnEnvironmentQuery,
  Mode,
  WorkspaceChallengeFragment,
} from 'types/graphql';

import { AttemptsLegacy } from './AttemptsLegacy';

gql`
  fragment WorkspaceChallenge on Challenge {
    type
    answers

    attempts {
      timestamp
      ...AttemptsChallengeAttempt
    }

    tabs {
      id
      index
      type
      title
      target
      new_window
      ...TabContentChallengeTab
    }
  }
`;

export interface WorkspaceLegacyProps {
  mode: Mode;
  teamSlug: string;
  isPreview?: boolean;
  track: NonNullable<LearnEnvironmentLegacyQuery['track']>;
  challenge: WorkspaceChallengeFragment;
  team: NonNullable<LearnEnvironmentQuery['team']>;
  expired: boolean;
  onClickExpand(): void;
  onRefreshTab(tabType: ChallengeTabType): void;
  sidebarCollapsed: boolean;
  onQuizAnswerChange(e: React.ChangeEvent<HTMLInputElement>, index: number): void;
  captureFocusOnServiceTabs?: boolean;
  isResizing: boolean;
}

export const WorkspaceLegacy = ({
  track,
  isPreview,
  teamSlug,
  challenge: { attempts, answers, tabs, type: challengeType },
  expired,
  team,
  onClickExpand,
  onRefreshTab,
  sidebarCollapsed,
  onQuizAnswerChange,
  captureFocusOnServiceTabs,
  isResizing,
  mode,
}: WorkspaceLegacyProps) => {
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const sortedTabs = useMemo(() => tabs.slice().sort((a, b) => a.index - b.index), [tabs]);

  const tabWindowMapRef = useRef<Map<string, Window | null>>(new Map());
  const [activeTabId, setActiveTabId] = useState<string | undefined>(sortedTabs[0]?.id);
  const [expandedAttempts, setExpandedAttempts] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const activeTab = sortedTabs.find(t => t.id === activeTabId);

  const onClickTab = (id: string) => {
    const tab = sortedTabs.find(t => t.id === id)!;

    if (!tab.new_window && tab.type !== ChallengeTabType.External) {
      setActiveTabId(id);
      return;
    }

    const tabWindowMap = tabWindowMapRef.current;
    const tabWindowName = `tab-${id}`;
    const existingTabWindow = tabWindowMap.get(tabWindowName);

    if (existingTabWindow && !existingTabWindow.closed) {
      existingTabWindow.focus();
      trackAmplitudeEvent({ name: 'Lab - Challenge - Opened external window', data: { action: 'focused' } });
    } else if (tab.target) {
      tabWindowMap.set(tabWindowName, window.open(tab.target, tabWindowName));
      trackAmplitudeEvent({ name: 'Lab - Challenge - Opened external window', data: { action: 'opened' } });
    }
  };

  const onClickRefresh = () => {
    if (!activeTab) {
      return;
    }

    onRefreshTab(activeTab.type);

    const iframe = document.getElementById(`challenge-frame-${activeTabId}`) as HTMLIFrameElement | null;

    if (!iframe || refreshing) {
      return;
    }

    setRefreshing(true);

    const { src } = iframe;
    iframe.src = '';

    setTimeout(() => {
      setRefreshing(false);
      iframe.src = src;
    }, 1000);
  };

  return (
    <>
      <HeaderLegacy
        hideRefresh={!activeTab || activeTab.type === ChallengeTabType.Code}
        hideExpand={!sidebarCollapsed}
        onClickExpand={onClickExpand}
        onClickRefresh={onClickRefresh}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            title={tab.title}
            type={tab.type}
            newWindow={tab.new_window}
            active={tab.id === activeTabId}
            onClickTab={() => onClickTab?.(tab.id)}
          />
        ))}
      </HeaderLegacy>

      {expired ? (
        <ExpiredScreen mode={mode} track={track} teamSlug={team.slug} />
      ) : (
        <TabContent
          isPreview={isPreview}
          tabs={sortedTabs}
          activeTabId={activeTabId}
          answers={answers}
          challengeType={challengeType}
          captureFocusOnServiceTabs={captureFocusOnServiceTabs}
          isResizing={isResizing}
          onQuizAnswerChange={onQuizAnswerChange}
        />
      )}

      <AttemptsLegacy
        expanded={expandedAttempts}
        attempts={attempts.slice().sort((a, b) => compareDesc(parseISO(a.timestamp), parseISO(b.timestamp)))}
        onToggleClick={() => setExpandedAttempts(s => !s)}
      />
    </>
  );
};
